import { createContext, useContext, type ReactNode } from 'react';
import type { OpenapiQueryClient } from 'openapi-react-query';
import type { PentaApiPaths } from './pentaApiClient';

type PentaRqClient = OpenapiQueryClient<PentaApiPaths>;

const PentaApiClientContext = createContext<PentaRqClient | null>(null);

export interface PentaApiClientProviderProps {
  client: PentaRqClient;
  children: ReactNode;
}

/**
 * Provider for the Penta API Client (React Query version).
 * Wraps the application to provide access to the API client hooks via `usePentaRq`.
 */
export const PentaApiClientProvider = ({
  client,
  children,
}: PentaApiClientProviderProps) => {
  return (
    <PentaApiClientContext.Provider value={client}>
      {children}
    </PentaApiClientContext.Provider>
  );
};

/**
 * Hook to access the Penta API Client.
 * Returns the object containing all API endpoints with .useQuery(), .useMutation(), etc.
 *
 * Example:
 * const api = usePentaRq();
 * const { data } = api.users.getUser.useQuery(["params"]);
 */
export const usePentaRq = (): PentaRqClient => {
  const client = useContext(PentaApiClientContext);
  if (!client) {
    throw new Error('usePentaRq must be used within a PentaApiClientProvider');
  }
  return client;
};
